// angular imports
import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';

// cc-common imports
import { IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// devextreme imports
import { DxDataGridComponent } from 'devextreme-angular';
import { formatNumber } from 'devextreme/localization';
import { Column } from 'devextreme/ui/data_grid';

// service imports
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { WorkflowSharedService } from '../../_services/workflow-shared.service';
import { PrecisionService } from '../../../_services/shared-services/precision.service';

// model imports
import { BaseAPBillItemRawDto } from '../../../cirabooks/shared-cirabooks-module/_models/accounts-payable/ap-bill/base-ap-bill-item-raw-dto';
import { WFEBudgetedGLAccountSelectorResultDto } from '../../_models/wfe-budgeted-gl-account-selector-result-dto';
import { DimensionOptionCodeRawDto } from '../../../cirabooks/shared-cirabooks-module/_models/common/dimensions/dimension-option-code-raw-dto';
import { DimensionSelectorResultDto } from '../../../cirabooks/shared-cirabooks-module/_models/common/dimensions/dimension-selector-result-dto';
import { GLSubcategorySelectorResultDto } from '../../../cirabooks/shared-cirabooks-module/_models/common/gl-account/glsubcategory-selector-result-dto';
import { SubCategoryByGlAccountParametersDto } from '../../../cirabooks/shared-cirabooks-module/_models/common/subcategory-by-gl-account-parametere-dto';
import { DimensionOptionResultDto } from '../../../cirabooks/shared-cirabooks-module/_models/common/dimensions/dimension-option-result-dto';

// constants imports
import { ActionConstants } from '../../../_constants/action-constants';
import { StringFormatConstants } from '../../../_constants/string-format-constants';

@Component({
    selector: 'app-workflow-invoice-line-items-editor',
    templateUrl: './workflow-invoice-line-items-editor.component.html',
    styleUrls: ['./workflow-invoice-line-items-editor.component.scss']
})
export class WorkflowInvoiceLineItemsEditorComponent implements OnInit, OnChanges {
    @ViewChild('lineItemGrid', { static: false }) lineItemGrid: DxDataGridComponent;
    @ViewChild('lineItemEditorMessageBox', { static: false }) lineItemEditorMessageBox: MessageBoxComponent;

    @Input() lineItems: Array<BaseAPBillItemRawDto> = [];
    @Input() glAccountsBudgetInfo: Array<WFEBudgetedGLAccountSelectorResultDto> = [];
    @Input() dimensionSource: Array<DimensionSelectorResultDto> = [];
    @Input() managementCompanyId: number;
    @Input() communityId: number;
    @Input() invoiceAmount: number;
    @Input() entityUsesSubCategory: boolean;
    @Input() allowDimensionEdit = true;
    @Input() requireDimensionMatch: boolean;
    @Input() requiredDimension: DimensionOptionResultDto;
    @Input() description: string;

    gridLineItems: Array<BaseAPBillItemRawDto> = [];

    currencyFormat: string;

    iconPlus = IconConstants.ADD;
    iconDelete = IconConstants.DELETE;

    glAccountColumns: Array<Column> = [];
    subCategoryColumns: Array<Column> = [];

    constructor(
        private readonly workflowSharedService: WorkflowSharedService,
        private readonly errorService: ServerErrorService,
        private readonly precisionService: PrecisionService
    ) {
        this.customizeLineItemSummary = this.customizeLineItemSummary.bind(this);
    }

    ngOnInit() {
        this.currencyFormat = StringFormatConstants.currencyFormat;
        if (Utils.notNullOrEmpty(this.lineItems)) {
            this.prepareLineItems();
        } else {
            this.addRawLineItem();
        }

        this.prepareGLAccountColumns();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (Utils.notNullAndDefined(changes.description) && Utils.notNullOrEmpty(changes.description.currentValue)) {
            this.updateDescription();
        }
    }

    onEditorContentReady(event: any): void {
        event.component.filter(['crudAction', '<>', ActionConstants.DELETE]);
    }

    onRowUpdated(event: any): void {
        const rowData = event.data;
        const dimensions: DimensionOptionCodeRawDto[] = [];
        this.gridLineItems.forEach((item) => {
            if (Utils.equals(item.itemId, rowData.itemId)) {
                this.dimensionSource.forEach((row) => {
                    const obj: DimensionOptionCodeRawDto = new DimensionOptionCodeRawDto();
                    obj.dimensionCode = row.code;
                    obj.dimensionOptionCode = rowData[row.code];
                    dimensions.push(obj);
                });
                item.dimensions = dimensions;
                item.crudAction = Utils.notNullOrEmpty(item.crudAction) ? item.crudAction : ActionConstants.EDIT;
            }
        });
    }

    onAddNewLineItem(): void {
        this.addRawLineItem();
    }

    onDeleteClick(item: BaseAPBillItemRawDto): void {
        if (Utils.isNullOrUndefined(item.apBillItemId)) {
            this.gridLineItems = this.gridLineItems.filter((d) => Utils.notEquals(d.itemId, item.itemId));
        } else {
            this.gridLineItems.forEach((ele) => {
                if (Utils.equals(ele.apBillItemId, item.apBillItemId)) {
                    ele.crudAction = ActionConstants.DELETE;
                }
            });
        }

        this.lineItemGrid.instance.filter(['crudAction', '<>', ActionConstants.DELETE]);
        this.lineItemGrid.instance.refresh();
    }

    onGLAccountSelection(account: WFEBudgetedGLAccountSelectorResultDto[], cellInfo: any): void {
        if (Utils.notNullOrEmpty(account)) {
            if (Utils.isTrue(this.entityUsesSubCategory)) {
                this.populateSubcategories(account[0].glAccountNumber, cellInfo.data);
            }
        }
    }

    onSubCategorySelection(subCategory: GLSubcategorySelectorResultDto[], cellInfo: any): void {
        if (Utils.notNullOrEmpty(subCategory)) {
            cellInfo.setValue(subCategory[0].id);
        } else {
            cellInfo.setValue(null);
        }
    }

    customizeLineItemSummary(e: any): string {
        return 'Total: ' + formatNumber(e.value, this.currencyFormat);
    }

    private updateDescription(): void {
        this.gridLineItems.forEach((row) => {
            row.description = this.description;
            row.crudAction = ActionConstants.EDIT;
        });
    }

    private populateSubcategories(glAccountNumber: string, rawItem: any, clearCache = false): void {
        const baseParam: SubCategoryByGlAccountParametersDto = new SubCategoryByGlAccountParametersDto();
        baseParam.managementCompanyId = this.managementCompanyId;
        baseParam.communityId = this.communityId;
        baseParam.glAccountNumber = glAccountNumber;
        baseParam.clearCache = clearCache;

        this.workflowSharedService.getSubCategories(baseParam).subscribe(
            (result: GLSubcategorySelectorResultDto[]) => {
                rawItem.subCategorySource = result;

                const subCategory = result.find((d) => Utils.equals(d.id, rawItem.subCategoryId));
                rawItem.subCategoryId = Utils.notNullAndDefined(subCategory) ? subCategory.id : null;
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private prepareLineItems(): void {
        const gridItems: Array<BaseAPBillItemRawDto> = [];
        this.lineItems.forEach((lineItem) => {
            let obj = new BaseAPBillItemRawDto();
            obj.itemId = this.lineItems.indexOf(lineItem);
            obj = Object.assign(obj, lineItem);
            if (Utils.isTrue(this.entityUsesSubCategory)) {
                this.populateSubcategories(obj.glAccountNumber, obj);
            }
            gridItems.push(obj);
        });

        this.gridLineItems = gridItems;
        this.prepareDimensions();
    }

    private addRawLineItem(): void {
        const item: BaseAPBillItemRawDto = new BaseAPBillItemRawDto();
        item.itemAmount = 0;
        item.itemId = Utils.notNullOrEmpty(this.gridLineItems)
            ? Math.max.apply(
                  Math,
                  this.gridLineItems.map((o) => {
                      return o.itemId;
                  })
              ) + 1
            : 1;
        item.crudAction = ActionConstants.ADD;
        item.apBillItemId = null;
        item.description = null;

        this.gridLineItems.push(item);
        this.prepareDimensions();
    }

    private prepareDimensions(): void {
        if (Utils.notNullOrEmpty(this.gridLineItems)) {
            this.gridLineItems.forEach((item) => {
                const dimensions: DimensionOptionCodeRawDto[] = [];
                this.dimensionSource.forEach((dimNode) => {
                    let itemDimension: DimensionOptionCodeRawDto;

                    if (Utils.notNullOrEmpty(item.dimensions)) {
                        itemDimension = item.dimensions.find((d) =>
                            Utils.equalsIgnoreCase(d.dimensionCode, dimNode.code)
                        );
                    }
                    if (Utils.notNullAndDefined(itemDimension)) {
                        if (Utils.notNullAndDefined(itemDimension.dimensionOptionCode)) {
                            const selectedNode = dimNode.options.find((d) =>
                                Utils.equalsIgnoreCase(d.code, itemDimension.dimensionOptionCode)
                            );
                            item[itemDimension.dimensionCode] = Utils.notNullAndDefined(selectedNode)
                                ? selectedNode.code
                                : null;
                        } else if (Utils.isNullOrUndefined(item[itemDimension.dimensionCode])) {
                            item[itemDimension.dimensionCode] = null;
                        }
                    } else {
                        const obj: DimensionOptionCodeRawDto = new DimensionOptionCodeRawDto();
                        obj.dimensionCode = dimNode.code;
                        obj.dimensionOptionCode = null;
                        dimensions.push(obj);
                        if (Utils.notNullAndDefined(this.requiredDimension)) {
                            item[this.requiredDimension.dimensionCode] = this.requiredDimension.dimensionOptionCode;
                        } else {
                            item[dimNode.code] = null;
                        }
                        item.dimensions = dimensions;
                    }
                });
            });
        }
    }

    private checkValidity(): any {
        const resultStatus = { isValid: true, message: null };

        this.gridLineItems.forEach((data, index) => {
            const itemIndex = index + 1;
            if (data.itemAmount === 0 || Utils.isNullOrUndefined(data.itemAmount)) {
                resultStatus.isValid = false;
                resultStatus.message = `Line Item Amount must be different from zero for ItemId #${itemIndex}`;
            } else if (Utils.isNullOrUndefined(data.glAccountNumber)) {
                resultStatus.isValid = false;
                resultStatus.message = `The GL Account number is Required for ItemId #${itemIndex}`;
            } else if (
                Utils.isTrue(this.entityUsesSubCategory) &&
                Utils.isNullOrUndefined(data.subCategoryId) &&
                Utils.notNullOrEmpty(data.subCategorySource)
            ) {
                resultStatus.isValid = false;
                resultStatus.message = `The Sub Category code is Required for ItemId #${itemIndex}`;
            }

            this.dimensionSource.forEach((dimension) => {
                if (Utils.isTrue(dimension.required)) {
                    const itemDimension = data.dimensions.find((d) =>
                        Utils.equalsIgnoreCase(d.dimensionCode, dimension.code)
                    );
                    if (
                        Utils.notNullAndDefined(itemDimension) &&
                        Utils.isNullOrUndefined(itemDimension.dimensionOptionCode)
                    ) {
                        resultStatus.isValid = false;
                        resultStatus.message = `${dimension.code} is Required for ItemId #${itemIndex}`;
                    }
                }
            });
        });

        return resultStatus;
    }

    private prepareGLAccountColumns(): void {
        this.glAccountColumns = [
            {
                dataField: 'glAccountNumber',
                dataType: 'string',
                caption: 'GL Account No'
            },
            {
                dataField: 'glAccountDisplayText',
                dataType: 'string',
                caption: 'Title'
            },
            {
                dataField: 'budgetAmount',
                dataType: 'number',
                caption: 'Budget',
                format: this.currencyFormat
            }
        ];

        this.subCategoryColumns = [
            {
                dataField: 'title',
                dataType: 'string',
                caption: 'Title'
            },
            {
                dataField: 'budgetAmount',
                dataType: 'number',
                caption: 'Budget',
                format: this.currencyFormat
            }
        ];
    }

    public getLineItems(): BaseAPBillItemRawDto[] {
        const dimensionKeysToDelete: string[] = [];
        let modifiedLineItems: BaseAPBillItemRawDto[] = [];
        let totalAmount = 0;

        const resultStatus = this.checkValidity();

        if (Utils.isTrue(resultStatus.isValid)) {
            if (Utils.notNullOrEmpty(this.gridLineItems)) {
                for (const lineItem of this.gridLineItems) {
                    const rawItem = Object.assign(new BaseAPBillItemRawDto(), lineItem);
                    totalAmount += Utils.notEqualsIgnoreCase(rawItem.crudAction, ActionConstants.DELETE)
                        ? rawItem.itemAmount
                        : 0;
                    if (Utils.notNullOrEmpty(rawItem.dimensions)) {
                        const dimensions: Array<DimensionOptionCodeRawDto> = [];
                        rawItem.dimensions.forEach((dim) => {
                            dim.dimensionOptionCode = rawItem[dim.dimensionCode];
                            dimensionKeysToDelete.push(dim.dimensionCode);

                            if (Utils.notNullAndDefined(dim.dimensionOptionCode)) {
                                dimensions.push(dim);
                            }
                        });

                        rawItem.dimensions = dimensions;
                    }
                    dimensionKeysToDelete.forEach((key) => {
                        delete rawItem[key];
                    });

                    modifiedLineItems.push(rawItem);
                }
                totalAmount = this.precisionService.getRoundedNumber(totalAmount);
                const invoiceAmount = this.precisionService.getRoundedNumber(this.invoiceAmount);
                if (totalAmount < invoiceAmount) {
                    this.lineItemEditorMessageBox.showErrorMessage(
                        'Error',
                        'The sum of the Line Item Amount(s) must equal the Amount to Pay.'
                    );
                    modifiedLineItems = [];
                }
            }
        } else {
            this.lineItemEditorMessageBox.showErrorMessage('Error', resultStatus.message);
        }
        return modifiedLineItems;
    }
}
